"use client";

import { useTranslation } from "react-i18next";
import { useCurrency } from "@/app/components/providers/CurrencyProvider";
import { formatPrice, getPriceForPlan } from "@/app/utils/currency-config";
import Reveal from "./Reveal";
import TiltCard from "./TiltCard";

export default function PassiveCandidatesSignal() {
  const { t } = useTranslation();
  const { currency } = useCurrency();

  const litePrice = formatPrice(getPriceForPlan(currency, "lite"), currency);
  const agencyPrice = formatPrice(5000, currency);
  // Stated in words: no reliable source backs a specific passive-candidate
  // percentage, so the section makes the point without one.
  const points = [
    {
      key: "passive",
      value: t("landing.passiveCandidates.stat1"),
      label: t("landing.passiveCandidates.stat1Label"),
    },
    {
      key: "social",
      value: t("landing.passiveCandidates.stat2"),
      label: t("landing.passiveCandidates.stat2Label"),
    },
    {
      key: "price",
      value: t("landing.passiveCandidates.stat3", { price: litePrice }),
      label: t("landing.passiveCandidates.stat3Label", { agencyPrice }),
    },
  ];

  return (
    <section
      aria-labelledby="passive-title"
      className="relative overflow-hidden py-20 lg:py-28"
    >
      <div className="relative mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-8">
        <Reveal className="lg:col-span-5">
          <h2
            id="passive-title"
            className="text-balance font-display text-[clamp(2rem,3.8vw,3.5rem)] font-semibold leading-[1.05] tracking-[-0.035em] text-signal-ink"
          >
            {t("landing.passiveCandidates.headline")}{" "}
            <span className="text-signal-accent">
              {t("landing.passiveCandidates.headlineAccent")}
            </span>
          </h2>
          <p className="mt-6 max-w-[52ch] text-pretty text-[17px] leading-[1.65] text-signal-muted">
            {t("landing.passiveCandidates.body")}
          </p>
        </Reveal>

        <Reveal delay={120} className="group lg:col-span-7">
          <TiltCard
            restX={2}
            restY={-4}
            strength={4}
            lift={14}
            className="glass-flat rounded-glassPanel p-5 sm:p-7"
          >
            <ul className="grid w-full gap-3">
              {points.map(point => (
                <li
                  key={point.key}
                  className="glass-inset flex items-start gap-4 rounded-glassInner px-5 py-4"
                >
                  <span
                    aria-hidden="true"
                    className="mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full bg-signal-accent"
                  />
                  <div>
                    <p className="text-[16px] font-semibold leading-snug text-signal-ink">
                      {point.value}
                    </p>
                    <p className="mt-1 text-pretty text-[14px] leading-snug text-signal-muted">
                      {point.label}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </TiltCard>
        </Reveal>
      </div>
    </section>
  );
}
